import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider
} from '@mui/material';
import { 
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Security as SecurityIcon,
  Key as KeyIcon,
  Timer as TimerIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { apiClient } from '../../services/api';

interface SecuritySettingsData {
  twoFactorEnabled: boolean;
  sessionTimeout: number;
  apiKeysEnabled: boolean;
}

interface SecuritySettingsProps {
  salonId: string;
}

const SecuritySettings: React.FC<SecuritySettingsProps> = ({ salonId }) => {
  const [settings, setSettings] = useState<SecuritySettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const { checkAccess, getRestrictions } = useSettings();
  
  const canWrite = checkAccess('security', 'write');
  const restrictions = getRestrictions('security');

  useEffect(() => {
    fetchSettings();
  }, [salonId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/settings/${salonId}/security`);
      
      if (response.success) {
        setSettings(response.data.settings);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canWrite || !settings) {
      setError('You do not have permission to modify these settings');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const response = await apiClient.put(`/settings/${salonId}/security`, settings);
      
      if (response.success) {
        setSuccess('Security settings updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update security settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (updates: Partial<SecuritySettingsData>) => {
    if (settings) {
      setSettings({ ...settings, ...updates });
    }
  };

  const formatSessionTimeout = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Alert severity="error">
        Failed to load security settings. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Security & Access Control</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {restrictions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {restrictions.map(restriction => (
                <Chip
                  key={restriction}
                  icon={<LockIcon />}
                  label={restriction.replace(/_/g, ' ')}
                  color="warning"
                  size="small"
                />
              ))}
            </Box>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchSettings}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Authentication Security */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SecurityIcon color="primary" />
                <Typography variant="h6">
                  Authentication Security
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemText
                    primary="Two-Factor Authentication (2FA)"
                    secondary="Add an extra layer of security to user accounts"
                  />
                  <ListItemSecondaryAction>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.twoFactorEnabled}
                          onChange={(e) => updateSettings({ twoFactorEnabled: e.target.checked })}
                          disabled={!canWrite}
                        />
                      }
                      label=""
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <Divider />
                
                <ListItem>
                  <ListItemText
                    primary="Session Timeout"
                    secondary={`Current: ${formatSessionTimeout(settings.sessionTimeout)}`}
                  />
                  <ListItemSecondaryAction>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={settings.sessionTimeout}
                        onChange={(e) => updateSettings({ sessionTimeout: Number(e.target.value) })}
                        disabled={!canWrite}
                      >
                        <MenuItem value={1800}>30 minutes</MenuItem>
                        <MenuItem value={3600}>1 hour</MenuItem>
                        <MenuItem value={7200}>2 hours</MenuItem>
                        <MenuItem value={14400}>4 hours</MenuItem>
                        <MenuItem value={28800}>8 hours</MenuItem>
                        <MenuItem value={86400}>24 hours</MenuItem>
                      </Select>
                    </FormControl>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* API Security */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <KeyIcon color="primary" />
                <Typography variant="h6">
                  API Security
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemText
                    primary="API Keys"
                    secondary="Enable API access for third-party integrations"
                  />
                  <ListItemSecondaryAction>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.apiKeysEnabled}
                          onChange={(e) => updateSettings({ apiKeysEnabled: e.target.checked })}
                          disabled={!canWrite}
                        />
                      }
                      label=""
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>

              {settings.apiKeysEnabled && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    API Access Enabled
                  </Typography>
                  <Typography variant="body2">
                    API keys allow third-party applications to access your salon data. 
                    Only enable this if you need to integrate with external services.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Security Status */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ShieldIcon color="primary" />
                <Typography variant="h6">
                  Security Status
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color={settings.twoFactorEnabled ? 'success.main' : 'warning.main'}>
                      {settings.twoFactorEnabled ? '✓' : '!'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Two-Factor Auth
                    </Typography>
                    <Chip 
                      label={settings.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      color={settings.twoFactorEnabled ? 'success' : 'warning'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color="info.main">
                      <TimerIcon />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Session Timeout
                    </Typography>
                    <Chip 
                      label={formatSessionTimeout(settings.sessionTimeout)}
                      color="info"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color={settings.apiKeysEnabled ? 'primary.main' : 'text.disabled'}>
                      <KeyIcon />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      API Access
                    </Typography>
                    <Chip 
                      label={settings.apiKeysEnabled ? 'Enabled' : 'Disabled'}
                      color={settings.apiKeysEnabled ? 'primary' : 'default'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color="success.main">
                      A+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Security Score
                    </Typography>
                    <Chip 
                      label="Excellent"
                      color="success"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Security Recommendations */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Security Recommendations
              </Typography>
              
              <List>
                {!settings.twoFactorEnabled && (
                  <ListItem>
                    <ListItemText
                      primary="Enable Two-Factor Authentication"
                      secondary="Protect your account with an additional security layer"
                    />
                    <ListItemSecondaryAction>
                      <Chip label="Recommended" color="warning" size="small" />
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
                
                {settings.sessionTimeout > 14400 && (
                  <ListItem>
                    <ListItemText
                      primary="Reduce Session Timeout"
                      secondary="Consider shorter session timeouts for better security"
                    />
                    <ListItemSecondaryAction>
                      <Chip label="Consider" color="info" size="small" />
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
                
                <ListItem>
                  <ListItemText
                    primary="Regular Password Updates"
                    secondary="Encourage users to update passwords regularly"
                  />
                  <ListItemSecondaryAction>
                    <Chip label="Best Practice" color="success" size="small" />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {canWrite && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SecuritySettings;